import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, FileDown, Loader2, Share2 } from "lucide-react";
import { api, errMsg, downloadFile } from "../../lib/api";
import { ddmmyyyy, num } from "../../lib/format";
import { whatsappLink } from "../../lib/whatsapp";

export function challanMessage(c, company = "Man & Moda Garments") {
  const lines = [
    `*DELIVERY CHALLAN* · ${company}`,
    `Challan No: ${c.dc_no}`,
    `Date: ${ddmmyyyy(c.date)}`,
    `From: ${c.from_location}`,
    `To: ${c.to_location}`,
    `Vehicle: ${c.vehicle || "-"}`,
    "",
    "*Items*:",
    ...c.items.map((i, n) => `${n + 1}. ${i.description}${i.code ? ` [${i.code}]` : ""}${i.style ? ` (${i.style})` : ""} — ${num(i.qty)} pcs${c.status === "Received" ? `, received ${num(i.received)}${i.short ? `, short ${num(i.short)}` : ""}` : ""}`),
    "",
    `*Total: ${num(c.qty_total)} shirts*`,
  ];
  if (c.status === "Received") lines.push(`Received on ${ddmmyyyy(c.received_date)}: ${num(c.received_total)}${c.short_total ? `, short ${num(c.short_total)}` : ""}`);
  if (c.remarks) lines.push(`Remarks: ${c.remarks}`);
  return lines.join("\n");
}

export const ChallanShare = ({ c, company }) => {
  const [busy, setBusy] = useState("");
  const pending = useRef(null);
  const prefetch = () => {
    pending.current = pending.current || api.get(`/challans/${c.id}/pdf`, { responseType: "blob" });
  };
  const pdf = async () => {
    setBusy("pdf");
    try {
      await downloadFile(`/challans/${c.id}/pdf`, `${c.dc_no}.pdf`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const share = async () => {
    const msg = challanMessage(c, company);
    const probe = new File([""], "x.pdf", { type: "application/pdf" });
    if (!navigator.canShare?.({ files: [probe] })) {
      window.open(whatsappLink(msg), "_blank", "noopener");
      toast.info("This browser can't attach files. Download the PDF and attach it in WhatsApp.");
      return;
    }
    setBusy("share");
    try {
      prefetch();
      const r = await pending.current;
      const file = new File([r.data], `${c.dc_no}.pdf`, { type: "application/pdf" });
      await navigator.share({ files: [file], title: c.dc_no, text: msg });
    } catch (e) {
      pending.current = null;
      if (e?.name !== "AbortError") toast.error("Could not share. Use PDF and attach it in WhatsApp.");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="grid grid-cols-2 gap-2 pt-2">
      <button className="btn btn-wa" onPointerDown={prefetch} onClick={share} disabled={!!busy} data-testid={`challan-${c.dc_no}-share-btn`}>
        {busy === "share" ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Share
      </button>
      <button className="btn btn-outline" onClick={pdf} disabled={!!busy} data-testid={`challan-${c.dc_no}-pdf-btn`}>
        {busy === "pdf" ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} PDF
      </button>
    </div>
  );
};

export const MinCell = ({ location, item, onSaved }) => {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(item.min || "");
  const t = `min-${location}-${item.description}-${item.style}`.replace(/\s+/g, "_");
  const save = async () => {
    try {
      await api.put("/stock-mins", { location, key: item.key, min: Number(val) || 0 });
      setEdit(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!edit) {
    return (
      <button type="button" className="text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => setEdit(true)} data-testid={`${t}-edit`}>
        {item.min ? `Min ${num(item.min)}` : "Set min"}
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <input className="field mono !min-h-[40px] !w-20 !px-2" type="number" inputMode="numeric" min="0" value={val} autoFocus
        onChange={(e) => setVal(e.target.value)} data-testid={`${t}-input`} />
      <button type="button" className="btn btn-primary !min-h-[40px] px-3 text-xs" onClick={save} data-testid={`${t}-save`}>OK</button>
    </span>
  );
};

export const StockAlerts = ({ alerts }) => {
  if (!alerts?.length) return null;
  return (
    <div className="rise space-y-2 rounded-[14px] border border-destructive/40 bg-destructive/5 p-4" data-testid="stock-alerts">
      <p className="flex items-center gap-2 font-heading font-bold text-destructive">
        <AlertTriangle size={18} /> {alerts.length} item{alerts.length === 1 ? "" : "s"} below minimum stock
      </p>
      <div className="divide-y divide-destructive/15">
        {alerts.map((a, i) => (
          <div key={i} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`stock-alert-${i}`}>
            <span className="min-w-0"><b>{a.location}</b>{a.location_kind ? <span className="text-xs text-muted-foreground"> · {a.location_kind}</span> : null}
              <span className="block truncate text-xs text-muted-foreground">{a.description}{a.style ? ` · ${a.style}` : ""}</span></span>
            <span className="mono shrink-0 text-right text-xs"><b className="text-sm text-destructive">{num(a.qty)}</b> / min {num(a.min)}
              <span className="block text-muted-foreground">need {num(a.short_by)}</span></span>
          </div>
        ))}
      </div>
    </div>
  );
};
